/**
 * Bloc de Signature FTPE Outremer avec Tampon, Horodatage et QR Code
 * Pour le workflow d'adhésion à la Fédération des Très Petites Entreprises Outre-Mer
 */
import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CheckCircle, Clock, Shield, QrCode, Stamp, Calendar, MapPin, User, FileText, ExternalLink } from 'lucide-react';
import QRCode from 'react-qr-code';

// URL du tampon FTPE Outremer fourni par l'utilisateur
const FTPE_STAMP_URL = "https://customer-assets.emergentagent.com/job_credit-system-app/artifacts/std8c6gq_1.Stamp.png";

// Composant Tampon FTPE avec horodatage
const FTPEStamp = ({ 
  className = "", 
  size = "normal",
  dateSignature = null,
  lieuSignature = null,
  nomSignataire = null,
  showDetails = true
}) => {
  const formattedDate = dateSignature 
    ? new Date(dateSignature).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : null;

  return (
    <div className={`relative flex flex-col items-center ${className}`}>
      {/* Tampon FTPE */}
      <div className="relative">
        <img 
          src={FTPE_STAMP_URL} 
          alt="Tampon FTPE Outremer"
          className={size === "large" ? "w-48 h-auto" : size === "medium" ? "w-36 h-auto" : "w-28 h-auto"}
          style={{ 
            filter: 'drop-shadow(2px 2px 6px rgba(0,0,0,0.2))',
            maxWidth: size === "large" ? '200px' : size === "medium" ? '150px' : '120px'
          }}
        />
        {/* Badge de validation */}
        {dateSignature && (
          <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full p-1 shadow-lg">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
        )}
      </div>
      
      {/* Détails de l'horodatage */}
      {showDetails && (dateSignature || lieuSignature || nomSignataire) && (
        <div className="mt-4 text-center space-y-1">
          {nomSignataire && (
            <div className="flex items-center justify-center gap-1 text-sm text-slate-700">
              <User className="w-3 h-3" />
              <span className="font-medium">{nomSignataire}</span>
            </div>
          )}
          {formattedDate && (
            <div className="flex items-center justify-center gap-1 text-xs text-slate-600">
              <Calendar className="w-3 h-3" />
              <span>{formattedDate}</span>
            </div>
          )}
          {lieuSignature && (
            <div className="flex items-center justify-center gap-1 text-xs text-slate-500">
              <MapPin className="w-3 h-3" />
              <span>{lieuSignature}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Composant QR Code de vérification
const VerificationQRCode = ({ adhesionId, verificationUrl, size = 100 }) => {
  const url = verificationUrl || `${window.location.origin}/verify/ftpe/${adhesionId}`;
  
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="bg-white p-3 rounded-lg border-2 border-slate-200 shadow-sm">
        <QRCode 
          value={url}
          size={size}
          level="M"
          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
        />
      </div>
      <div className="text-center">
        <p className="text-xs text-slate-500">Scanner pour vérifier</p>
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-xs text-blue-600 hover:underline flex items-center justify-center gap-1"
        >
          <ExternalLink className="w-3 h-3" />
          Lien de vérification
        </a>
      </div>
    </div>
  );
};

// Bloc de signature complet pour l'adhésion FTPE
export default function SignatureFTPEBlock({ 
  adhesion,
  onSign,
  isSignable = false,
  signatureData = null,
  showQRCode = true,
  compact = false
}) {
  const [accepted, setAccepted] = useState(false);
  const [signing, setSigning] = useState(false);

  // Données de signature
  const dateSignature = signatureData?.date || adhesion?.date_signature;
  const lieuSignature = signatureData?.lieu || adhesion?.lieu_signature;
  const nomSignataire = signatureData?.nom || adhesion?.representant_nom || adhesion?.nom_complet;
  const adhesionId = adhesion?.id || adhesion?.numero_adhesion;
  
  // Générer URL de vérification
  const getVerificationUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/verify/ftpe/${adhesionId}`;
  };

  // Gérer la signature
  const handleSign = async () => {
    if (!accepted) return;
    setSigning(true);
    try {
      if (onSign) {
        await onSign();
      }
    } finally {
      setSigning(false);
    }
  };

  // Mode compact pour affichage dans PDF ou récapitulatif
  if (compact) {
    return (
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
        <div className="flex items-center gap-4">
          <FTPEStamp size="small" showDetails={false} />
          <div>
            <p className="font-semibold text-slate-800">Adhésion signée électroniquement</p>
            {dateSignature && (
              <p className="text-sm text-slate-600">
                Le {new Date(dateSignature).toLocaleDateString('fr-FR')} à {lieuSignature || 'N/A'}
              </p>
            )}
            {nomSignataire && (
              <p className="text-sm text-slate-500">Par {nomSignataire}</p>
            )}
          </div>
        </div>
        {showQRCode && adhesionId && (
          <VerificationQRCode adhesionId={adhesionId} size={60} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Séparateur */}
      <div className="border-t-2 border-dashed border-amber-300 pt-6">
        <h3 className="text-lg font-bold text-center text-slate-800 mb-6 flex items-center justify-center gap-2">
          <Stamp className="w-5 h-5 text-amber-600" />
          SIGNATURE ET AUTHENTIFICATION
        </h3>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Signature FTPE */}
        <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-4 py-2">
            <Badge className="bg-white text-slate-800/20 text-white border-0 font-semibold">
              FTPE Outremer
            </Badge>
          </div>
          <CardContent className="pt-6">
            <div className="text-center mb-4">
              <p className="text-sm text-slate-600">Fédération des Très Petites Entreprises</p>
              <p className="text-xs text-slate-500">Syndicat patronal ultramarin</p>
            </div>

            <div className="flex flex-col items-center gap-4">
              {/* Tampon FTPE */}
              <FTPEStamp 
                size="large" 
                dateSignature={dateSignature}
                lieuSignature={lieuSignature}
                nomSignataire={nomSignataire}
              />

              {/* Statut de signature */}
              {dateSignature ? (
                <div className="flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full text-green-700">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Signature validée</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full text-amber-700">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm font-medium">En attente de signature</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* QR Code de vérification */}
        <Card className="border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-white">
          <div className="bg-gradient-to-r from-slate-600 to-slate-700 px-4 py-2">
            <Badge className="bg-white text-slate-800/20 text-white border-0 font-semibold flex items-center gap-1">
              <QrCode className="w-3 h-3" />
              Vérification
            </Badge>
          </div>
          <CardContent className="pt-6">
            <div className="text-center mb-4">
              <p className="text-sm text-slate-600">Code QR d'authenticité</p>
              <p className="text-xs text-slate-500">Scannez pour vérifier la validité</p>
            </div>

            <div className="flex justify-center">
              {adhesionId ? (
                <VerificationQRCode 
                  adhesionId={adhesionId} 
                  verificationUrl={getVerificationUrl()}
                  size={120}
                />
              ) : (
                <div className="w-32 h-32 bg-slate-100 rounded-lg flex items-center justify-center">
                  <p className="text-xs text-slate-400 text-center px-2">
                    QR code généré après signature
                  </p>
                </div>
              )}
            </div>

            {/* Informations de sécurité */}
            <div className="mt-4 p-3 bg-slate-100 rounded-lg">
              <div className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-slate-600 mt-0.5" />
                <div className="text-xs text-slate-600">
                  <p className="font-medium mb-1">Signature sécurisée</p>
                  <p>Ce document est authentifié électroniquement et horodaté conformément au règlement eIDAS.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bouton de signature (si signable) */}
      {isSignable && !dateSignature && (
        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                id="accept-signature"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="w-5 h-5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
              />
              <label htmlFor="accept-signature" className="text-sm text-slate-700 cursor-pointer">
                J'ai lu et j'accepte les termes du document d'adhésion à la FTPE Outremer.
                Je confirme l'exactitude des informations fournies.
              </label>
            </div>
            
            <Button 
              onClick={handleSign}
              disabled={!accepted || signing}
              className="w-full h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold"
            >
              {signing ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Signature en cours...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Signer électroniquement mon adhésion
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Mentions légales */}
      <div className="text-center text-xs text-slate-500 space-y-1">
        <p>Document signé électroniquement conformément à l'article 1367 du Code civil.</p>
        <p>Horodatage certifié • Conservation sécurisée • Valeur probante garantie</p>
      </div>
    </div>
  );
}

// Export des sous-composants pour utilisation individuelle
export { FTPEStamp, VerificationQRCode };
